import { FileRejection } from 'react-dropzone';
import { CompressImageError, CompressImageResult, TARGET_MAX_BYTES } from 'shared/utils/image/compress-image';

/**
 * Тексты о том, что произошло с приложенным фото.
 *
 * Вынесены из компонента, потому что это единственный канал обратной связи по
 * фото: `DefaultDropzone` не рисует ошибки Formik (в v2 у него нет ни `useField`,
 * ни `meta.error`), поэтому всё говорится тостом.
 */

/** Предел на исходный файл: сверх этого не читаем в память даже ради сжатия. */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/**
 * Форматы Apple. Браузер их не декодирует, а сконвертировать на клиенте нечем,
 * поэтому единственное, что можно сделать, — объяснить, где переключить формат.
 */
const HEIC_MESSAGE =
  'Формат HEIC не поддерживается. Включите «Наиболее совместимый» в Настройки → Камера → Форматы, или выберите фото в JPEG';

const UNREADABLE_MESSAGE = 'Не удалось прочитать файл. Подойдут JPG, PNG или WebP';
const PROCESSING_FAILED_MESSAGE =
  'Не удалось обработать фото. Попробуйте другое изображение или отправьте заявку без фото';

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
const formatSize = (size: { width: number; height: number }) => `${size.width}×${size.height}`;

/** HEIC часто приезжает с пустым `type`, поэтому проверяется и расширение. */
export function isHeicFile(file: Pick<File, 'name' | 'type'>): boolean {
  return /^image\/heic|^image\/heif/.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

/** Почему файл не приняла сама дропзона (до какой-либо обработки). */
export function describePhotoRejection(rejection: FileRejection): string {
  if (isHeicFile(rejection.file)) return HEIC_MESSAGE;

  const codes = rejection.errors.map((error) => error.code);
  if (codes.includes('file-too-large')) {
    return `Файл слишком большой. Выберите фото до ${formatMb(MAX_SOURCE_BYTES)}`;
  }

  return UNREADABLE_MESSAGE;
}

/**
 * Что стало с фото после обработки. `undefined` — значит файл не трогали и
 * сообщать нечего.
 */
export function describeCompression(result: CompressImageResult): string | undefined {
  if (!result.wasCompressed || !result.originalSize || !result.resultSize) return undefined;

  const weights = `${formatMb(result.originalBytes)} → ${formatMb(result.resultBytes)}`;

  if (result.keptResolution) {
    return `Фото сжато: ${weights}. Разрешение ${formatSize(result.originalSize)} сохранено`;
  }

  return `Фото уменьшено: ${weights}, ${formatSize(result.originalSize)} → ${formatSize(result.resultSize)}`;
}

/** Почему обработка не удалась. `file` нужен, чтобы отличить HEIC от битого файла. */
export function describePhotoFailure(error: unknown, file: Pick<File, 'name' | 'type'>): string {
  if (!(error instanceof CompressImageError)) return PROCESSING_FAILED_MESSAGE;

  if (error.reason === 'decode-failed') {
    return isHeicFile(file) ? HEIC_MESSAGE : UNREADABLE_MESSAGE;
  }

  if (error.reason === 'too-large') {
    return `Не удалось сжать фото до ${formatMb(TARGET_MAX_BYTES)}. Выберите изображение меньшего размера`;
  }

  return PROCESSING_FAILED_MESSAGE;
}
